import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Mail, Phone, Users, Search, Filter, Calendar, ChevronUp, ChevronDown, Ticket as TicketIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';

interface CustomerBooking {
  booking_id: string;
  customer_name: string;
  customer_email: string;
  phone_number: string;
  participant_count: number;
  total_amount: number;
  discount_amount: number;
  booking_date: string;
  status: string;
  special_requirements?: string;
}

interface CustomerByExperience {
  customer_email: string;
  customer_name: string;
  phone_number: string;
  bookings: CustomerBooking[];
  total_bookings: number;
  total_tickets: number;
  total_spent: number;
  most_recent_booking: string;
}

interface ExperienceCustomers {
  experience_id: string;
  experience_title: string;
  customers: CustomerByExperience[];
}

interface BookingWithGuests {
  booking_id: string;
  experience_title: string;
  customer_name: string;
  customer_email: string;
  participant_count: number;
  total_amount: number;
  status: string;
  created_at: string;
  guest_data: any[];
  special_requirements: string;
}

export function CreatorCustomerManagement() {
  const { user, profile } = useUnifiedAuth();
  const [experienceCustomers, setExperienceCustomers] = useState<ExperienceCustomers[]>([]);
  const [recentBookings, setRecentBookings] = useState<BookingWithGuests[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<Record<string, { field: keyof CustomerByExperience; direction: 'asc' | 'desc' }>>({});

  useEffect(() => {
    if (user?.id) {
      fetchCustomerData();
    } else if (user === null) {
      // User is explicitly null (not authenticated)
      setLoading(false);
      setError('User not authenticated');
    }
    // If user is undefined, we're still loading auth state
  }, [user]);

  const fetchCustomerData = async () => {
    let mounted = true; // Prevent state updates on unmounted component
    
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('Fetching customer data for user:', user.id);

      // Get creator profile ID
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        throw new Error(`Creator profile error: ${profileError.message}`);
      }
      
      if (!creatorProfile) {
        throw new Error('Creator profile not found');
      }

      console.log('Creator profile found:', creatorProfile.id);

      // Get paid confirmed bookings with customer details
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          id,
          user_id,
          experience_id,
          participant_count,
          total_amount,
          discount_amount,
          status,
          payment_status,
          created_at,
          experience:experiences!inner(id, title, creator_profile_id),
          user:users!bookings_user_id_fkey(id, full_name, email, phone_number)
        `)
        .eq('experience.creator_profile_id', creatorProfile.id)
        .eq('payment_status', 'paid')
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false });

      if (bookingError) {
        console.error('Booking error:', bookingError);
        throw new Error(`Booking query error: ${bookingError.message}`);
      }

      console.log('Bookings found:', bookings?.length || 0);

      // Get recent paid bookings (including pending)
      const { data: recentBookings, error: recentError } = await supabase
        .from('bookings')
        .select(`
          id,
          user_id,
          experience_id,
          participant_count,
          total_amount,
          discount_amount,
          status,
          payment_status,
          created_at,
          special_requirements,
          experience:experiences!inner(id, title, creator_profile_id),
          user:users!bookings_user_id_fkey(id, full_name, email)
        `)
        .eq('experience.creator_profile_id', creatorProfile.id)
        .eq('payment_status', 'paid')
        .gte('created_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(50);

      if (recentError) {
        console.error('Recent bookings error:', recentError);
        throw new Error(`Recent bookings query error: ${recentError.message}`);
      }

      console.log('Recent bookings found:', recentBookings?.length || 0);

      // Only update state if component is still mounted
      if (!mounted) return;

      // Group bookings by experience, then by customer
      const experienceMap = new Map<string, Map<string, CustomerByExperience>>();
      
      bookings?.forEach(booking => {
        const user = booking.user as any;
        const experience = booking.experience as any;
        const experienceId = experience?.id;
        const experienceTitle = experience?.title || 'Unknown Experience';
        const customerEmail = user?.email || '';
        
        if (!experienceId || !customerEmail) return;
        
        // Initialize experience if not exists
        if (!experienceMap.has(experienceId)) {
          experienceMap.set(experienceId, new Map());
        }
        
        const experienceCustomers = experienceMap.get(experienceId)!;
        
        // Initialize customer if not exists for this experience
        if (!experienceCustomers.has(customerEmail)) {
          experienceCustomers.set(customerEmail, {
            customer_email: customerEmail,
            customer_name: user?.full_name || 'Unknown',
            phone_number: user?.phone_number || '',
            bookings: [],
            total_bookings: 0,
            total_tickets: 0,
            total_spent: 0,
            most_recent_booking: booking.created_at
          });
        }
        
        const customer = experienceCustomers.get(customerEmail)!;
        
        // Add this booking to the customer
        const totalAmount = booking.total_amount || 0;
        const discountAmount = booking.discount_amount || 0;
        const grossRevenue = totalAmount - discountAmount;
        
        customer.bookings.push({
          booking_id: booking.id,
          customer_name: customer.customer_name,
          customer_email: customerEmail,
          phone_number: customer.phone_number,
          participant_count: booking.participant_count || 1,
          total_amount: totalAmount,
          discount_amount: discountAmount,
          booking_date: booking.created_at,
          status: booking.status,
          special_requirements: (booking as any).special_requirements || ''
        });
        
        // Update customer totals
        customer.total_bookings += 1;
        customer.total_tickets += booking.participant_count || 1;
        customer.total_spent += grossRevenue;
        customer.most_recent_booking = booking.created_at > customer.most_recent_booking ? 
          booking.created_at : customer.most_recent_booking;
      });

      // Convert to array format and sort
      const experienceCustomersList: ExperienceCustomers[] = Array.from(experienceMap.entries()).map(([experienceId, customerMap]) => {
        const firstBooking = Array.from(customerMap.values())[0]?.bookings[0];
        const experienceTitle = bookings?.find(b => (b.experience as any)?.id === experienceId)?.experience?.[0]?.title || 'Unknown Experience';
        
        const customers = Array.from(customerMap.values()).sort((a, b) => 
          new Date(b.most_recent_booking).getTime() - new Date(a.most_recent_booking).getTime()
        );
        
        return {
          experience_id: experienceId,
          experience_title: experienceTitle,
          customers
        };
      }).sort((a, b) => {
        // Sort experiences by most recent booking across all customers
        const aMostRecent = Math.max(...a.customers.map(c => new Date(c.most_recent_booking).getTime()));
        const bMostRecent = Math.max(...b.customers.map(c => new Date(c.most_recent_booking).getTime()));
        return bMostRecent - aMostRecent;
      });
      
      // Transform recent bookings to match expected interface
      const transformedRecentBookings = recentBookings?.map(booking => ({
        booking_id: booking.id,
        experience_title: (booking.experience as any)?.title || 'Unknown Experience',
        customer_name: (booking.user as any)?.full_name || 'Unknown',
        customer_email: (booking.user as any)?.email || '',
        participant_count: booking.participant_count,
        total_amount: booking.total_amount,
        status: booking.status,
        created_at: booking.created_at,
        guest_data: [], // guest_data column doesn't exist in bookings table
        special_requirements: booking.special_requirements || ''
      })) || [];
      
      if (mounted) {
        setExperienceCustomers(experienceCustomersList);
        setRecentBookings(transformedRecentBookings);
        console.log('Customer data updated successfully');
      }

    } catch (err) {
      console.error('Failed to fetch customer data:', err);
      if (mounted) {
        setError(err instanceof Error ? err.message : 'Failed to load customer data');
      }
    } finally {
      if (mounted) {
        setLoading(false);
      }
    }

    // Cleanup function
    return () => {
      mounted = false;
    };
  };

  const exportAttendeesCSV = async () => {
    try {
      // Get creator profile
      const { data: creatorProfile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();
      
      if (!creatorProfile) {
        throw new Error('Creator profile not found');
      }

      // Get all tickets for creator's experiences
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          guest_name,
          guest_email,
          checked_in,
          checked_in_at,
          transfer_type,
          bookings!inner(
            experience_id,
            user:users!bookings_user_id_fkey(full_name, email),
            experience:experiences!inner(
              title,
              creator_profile_id
            )
          )
        `)
        .eq('bookings.experience.creator_profile_id', creatorProfile.id)
        .order('bookings.experience_id', { ascending: true })
        .order('ticket_number', { ascending: true });

      if (error) throw error;

      // Format CSV data
      const csvHeaders = ['Experience', 'Ticket #', 'Name', 'Email', 'Type', 'Checked In', 'Check-in Time'];
      const csvRows = tickets?.map(ticket => {
        const booking = ticket.bookings as any;
        const experience = booking?.experience;
        const isAllocated = ticket.transfer_type === 'allocation';
        const isTransferred = ticket.transfer_type === 'transfer';
        
        return [
          experience?.title || 'Unknown',
          ticket.ticket_number,
          ticket.guest_name || booking?.user?.full_name || 'N/A',
          ticket.guest_email || booking?.user?.email || 'N/A',
          isTransferred ? 'Transferred' : isAllocated ? 'Allocated' : 'Original',
          ticket.checked_in ? 'Yes' : 'No',
          ticket.checked_in_at ? new Date(ticket.checked_in_at).toLocaleString() : ''
        ];
      }) || [];

      // Create CSV content
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all-attendees-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export attendees:', err);
      alert('Failed to export attendee list');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };



  const exportCustomerData = async (e?: React.MouseEvent) => {
    console.log('=== EXPORT FUNCTION CALLED ===');
    e?.preventDefault(); // Prevent any form submission behavior
    e?.stopPropagation(); // Prevent event bubbling
    
    try {
      setExportLoading(true);
      
      console.log('Starting CSV export...');
      
      // Check if we have customers to export
      if (experienceCustomers.length === 0) {
        alert('No customer data to export');
        return;
      }
      
      // Get JWT from Supabase auth with timeout
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session timeout')), 10000)
      );
      
      const session = await Promise.race([sessionPromise, timeoutPromise]) as any;
      const jwt = session?.data?.session?.access_token;
      
      console.log('JWT Debug:', {
        hasSession: !!session?.data?.session,
        hasAccessToken: !!jwt,
        tokenLength: jwt?.length,
        tokenStart: jwt?.substring(0, 30) + '...',
        expiresAt: session?.data?.session?.expires_at,
        user: session?.data?.session?.user?.id
      });
      
      if (!jwt) {
        throw new Error('Not authenticated - please sign in again');
      }
      
      console.log('Got JWT, calling export function...');
      
      // Call Netlify function with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`/.netlify/functions/export-creator-bookings`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      console.log('Export response:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const blob = await response.blob();
      console.log('Got blob, downloading...');
      
      // Download CSV
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('CSV export completed successfully');
      
    } catch (err) {
      console.error('Failed to export data:', err);
      
      let errorMessage = 'Failed to export CSV. Please try again.';
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          errorMessage = 'Export timed out. Please try again.';
        } else if (err.message.includes('Session timeout')) {
          errorMessage = 'Session expired. Please refresh the page and try again.';
        } else {
          errorMessage = `Export failed: ${err.message}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setExportLoading(false);
      console.log('Export loading state cleared');
    }
  };

  const toggleCustomerExpansion = (experienceId: string, customerEmail: string) => {
    const key = `${experienceId}-${customerEmail}`;
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCustomers(newExpanded);
  };

  const handleSort = (experienceId: string, field: keyof CustomerByExperience) => {
    const currentSort = sortConfig[experienceId];
    const newDirection = currentSort?.field === field && currentSort?.direction === 'desc' ? 'asc' : 'desc';
    
    setSortConfig(prev => ({
      ...prev,
      [experienceId]: { field, direction: newDirection }
    }));
  };

  const sortCustomers = (customers: CustomerByExperience[], experienceId: string) => {
    const sortCfg = sortConfig[experienceId];
    if (!sortCfg) return customers;

    return [...customers].sort((a, b) => {
      let aValue = a[sortCfg.field];
      let bValue = b[sortCfg.field];
      
      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortCfg.field === 'most_recent_booking') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }
      
      if (aValue < bValue) return sortCfg.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortCfg.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ 
    experienceId, 
    field, 
    children 
  }: { 
    experienceId: string; 
    field: keyof CustomerByExperience; 
    children: React.ReactNode 
  }) => {
    const sortCfg = sortConfig[experienceId];
    const isActive = sortCfg?.field === field;
    
    return (
      <th 
        className="text-left p-4 text-sm font-medium text-sand-300 cursor-pointer hover:text-sand-200 transition-colors"
        onClick={() => handleSort(experienceId, field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive && (
            sortCfg.direction === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </th>
    );
  };

  const calculateTotals = (customers: CustomerByExperience[]) => {
    return customers.reduce((totals, customer) => ({
      totalBookings: totals.totalBookings + customer.total_bookings,
      totalTickets: totals.totalTickets + customer.total_tickets,
      totalRevenue: totals.totalRevenue + customer.total_spent
    }), { totalBookings: 0, totalTickets: 0, totalRevenue: 0 });
  };

  const filteredExperienceCustomers = experienceCustomers.map(experience => {
    const filteredCustomers = experience.customers.filter(customer =>
      customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const sortedCustomers = sortCustomers(filteredCustomers, experience.experience_id);
    
    return {
      ...experience,
      customers: sortedCustomers
    };
  }).filter(experience => experience.customers.length > 0);

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading customer data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchCustomerData} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-display text-sand-100">Customer Management</h2>
          <p className="text-sand-400 text-sm">
            {experienceCustomers.reduce((total, exp) => total + exp.customers.length, 0)} total customers • {recentBookings.length} recent bookings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            type="button"
            size="sm" 
            onClick={exportAttendeesCSV}
            disabled={false}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Attendees
          </Button>
          <Button 
            type="button"
            size="sm" 
            onClick={(e) => {
              try {
                e.preventDefault();
                e.stopPropagation();
                console.log('=== BUTTON CLICKED ===');
                console.log('Button clicked, customers:', experienceCustomers.length);
                console.log('Export loading:', exportLoading);
                console.log('User:', user?.id);
                
                // Simple validation before calling the function
                if (!user?.id) {
                  console.log('No user ID, showing alert');
                  alert('Not authenticated');
                  return false;
                }
                
                if (experienceCustomers.length === 0) {
                  console.log('No customers, showing alert');
                  alert('No customers to export');
                  return false;
                }
                
                console.log('Calling exportCustomerData...');
                exportCustomerData(e);
                return false;
              } catch (error) {
                console.error('Button click error:', error);
                alert('Button click failed: ' + error);
                return false;
              }
            }}
            disabled={exportLoading || experienceCustomers.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-sand-400" />
        <input
          type="text"
          placeholder="Search customers by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-earth-800/50 border border-earth-700/50 rounded-lg text-sand-100 placeholder-sand-400 focus:outline-none focus:border-earth-600"
        />
      </div>

      {/* Customer List by Experience */}
      <div className="space-y-8">
        {filteredExperienceCustomers.map((experience, expIndex) => {
          const totals = calculateTotals(experience.customers);
          
          return (
            <div key={experience.experience_id} className="bg-earth-800/50 rounded-lg border border-earth-700/50 overflow-hidden">
              <div className="bg-earth-800/80 p-4 border-b border-earth-700/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-display text-sand-100">{experience.experience_title}</h3>
                    <p className="text-sm text-sand-400">{experience.customers.length} customers</p>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-sand-100 font-medium">{totals.totalBookings}</p>
                      <p className="text-sand-400 text-xs">Total Bookings</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sand-100 font-medium">{totals.totalTickets}</p>
                      <p className="text-sand-400 text-xs">Total Tickets</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sand-100 font-medium font-mono">{formatCurrency(totals.totalRevenue)}</p>
                      <p className="text-sand-400 text-xs">Gross Revenue</p>
                    </div>
                  </div>
                </div>
              </div>
            
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-earth-800/60">
                    <tr>
                      <SortableHeader experienceId={experience.experience_id} field="customer_name">Customer</SortableHeader>
                      <th className="text-left p-4 text-sm font-medium text-sand-300">Contact</th>
                      <SortableHeader experienceId={experience.experience_id} field="total_bookings">Bookings</SortableHeader>
                      <SortableHeader experienceId={experience.experience_id} field="total_tickets">Tickets</SortableHeader>
                      <SortableHeader experienceId={experience.experience_id} field="total_spent">Revenue</SortableHeader>
                      <SortableHeader experienceId={experience.experience_id} field="most_recent_booking">Last Booking</SortableHeader>
                    </tr>
                  </thead>
                <tbody>
                  {experience.customers.map((customer, customerIndex) => {
                    const isExpanded = expandedCustomers.has(`${experience.experience_id}-${customer.customer_email}`);
                    const hasMultipleBookings = customer.bookings.length > 1;
                    
                    return (
                      <React.Fragment key={customer.customer_email}>
                        <motion.tr
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (expIndex * 0.1) + (customerIndex * 0.05) }}
                          className={`border-t border-earth-700/50 hover:bg-earth-700/20 ${hasMultipleBookings ? 'cursor-pointer' : ''}`}
                          onClick={() => hasMultipleBookings && toggleCustomerExpansion(experience.experience_id, customer.customer_email)}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {hasMultipleBookings && (
                                <ChevronDown className={`h-4 w-4 text-sand-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              )}
                              <div>
                                <p className="text-sand-100 font-medium">{customer.customer_name || 'N/A'}</p>
                                <p className="text-sm text-sand-400">{customer.customer_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-3 w-3 text-sand-400" />
                                <span className="text-sand-300">{customer.customer_email}</span>
                              </div>
                              {customer.phone_number && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Phone className="h-3 w-3 text-sand-400" />
                                  <span className="text-sand-300">{customer.phone_number}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-sand-400" />
                              <span className="text-sand-100">{customer.total_bookings}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <TicketIcon className="h-4 w-4 text-sand-400" />
                              <span className="text-sand-100">{customer.total_tickets}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="text-sand-100 font-mono">{formatCurrency(customer.total_spent)}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sand-300 text-sm">
                              {new Date(customer.most_recent_booking).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </td>
                        </motion.tr>
                        
                        {/* Expanded booking details */}
                        {isExpanded && hasMultipleBookings && (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <div className="bg-earth-900/50 border-t border-earth-700/30">
                                <div className="p-4">
                                  <h4 className="text-sm font-medium text-sand-200 mb-3">Booking Details</h4>
                                  <div className="space-y-2">
                                    {customer.bookings.map((booking, bookingIndex) => (
                                      <div key={booking.booking_id} className="flex items-center justify-between p-3 bg-earth-800/30 rounded-lg">
                                        <div className="flex items-center gap-4">
                                          <span className="text-sm text-sand-400">#{bookingIndex + 1}</span>
                                          <div>
                                            <p className="text-sm text-sand-200">
                                              {booking.participant_count} ticket{booking.participant_count !== 1 ? 's' : ''}
                                            </p>
                                            <p className="text-xs text-sand-400">
                                              {new Date(booking.booking_date).toLocaleDateString()}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm text-sand-200 font-mono">
                                            {formatCurrency(booking.total_amount - booking.discount_amount)}
                                          </p>
                                          <p className="text-xs text-sand-400 capitalize">{booking.status}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredExperienceCustomers.length === 0 && searchTerm && (
        <div className="text-center py-8">
          <p className="text-sand-400">No customers found matching "{searchTerm}"</p>
        </div>
      )}

      {experienceCustomers.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-sand-400 mx-auto mb-4" />
          <p className="text-sand-400 mb-4">No customers yet</p>
          <p className="text-sand-500 text-sm">Customers will appear here after they book your experiences</p>
        </div>
      )}
    </div>
  );
} 